import { ChangeEvent, DataDisplayEvent, DataSourceValidationEvent, DropdownSelectionEvent, LookupModelSearchEvent, LookupModelSelectionEvent, Switch, Textbox, ValidationResult } from "@mcleod/components";
import { AutogenLayoutCallListProfile } from "./autogen/AutogenLayoutCallListProfile";
import { StringUtil } from "@mcleod/core";
import { DuplicateIdProps, DuplicateId } from "@mcleod/dispatch";

const viewCallListProfileURL = "lme/crm/CallListProfile?mode=update&key=";

const DUPLICATE_VALIDATION_PROPS: DuplicateIdProps = {
    tableName: "call_list_profile",
    screenName: "Call List Profile",
    navigateToUrl: viewCallListProfileURL
}

export class CallListProfile extends AutogenLayoutCallListProfile {

    onLoad(): void | Promise<void> {
        DuplicateId.configureIdTextbox(this.textboxId, DUPLICATE_VALIDATION_PROPS);
    }

    textboxTypeOfOnDataDisplay(event: DataDisplayEvent): void {
        const typeOf = event.rowData?.getString("type_of") ?? null;
        this.configureOrderDays(typeOf);
    }

    textboxTypeOfAfterDropdownSelection(event: DropdownSelectionEvent): void {
        const typeOf = event.selection?.value ?? null;
        this.configureOrderDays(typeOf);
    }

    configureOrderDays(typeOf: string | null): void {
        this.textboxLastOrderDays.enabled = typeOf !== "P";
        if (this.textboxLastOrderDays.enabled === false) {
            this.textboxLastOrderDays.clear();
        }
    }
    textboxOperationsUserBeforeLookupModelSearch(event: LookupModelSearchEvent): void {
        event.filter.web_user_type = "in U,E";
    }

    textboxRespLevelOnLookupModelSelection(event: LookupModelSelectionEvent): void {
        this.clearRespCodes();
    }

    textboxRespLevelOnChange(event: ChangeEvent): void {
        const empty = StringUtil.isEmptyString(event.newValue);
        if (empty) {
            this.clearRespCodes();
        }
        this.textboxRespCode.enabled = !empty;
    }

    textboxRespLevelOnDataDisplay(event: DataDisplayEvent): void {
        const empty = this.activeRow?.isNull("resp_level") ?? true;
        this.textboxRespCode.enabled = !empty;
    }

    clearRespCodes(): void {
        this.textboxRespCode.clear();
        this.activeRow?.set("resp_code", null);
        this.mainDataSource.displayDataInBoundComponent(this.textboxRespCode);
    }

    textboxRespCodeBeforeLookupModelSearch(event: LookupModelSearchEvent): void {
        if (this.activeRow?.isNull("resp_level")) {
            event.stop();
            return;
        }
        event.filter.hierarchy_level = this.activeRow?.getNumber("resp_level");
    }

    switchIncludeProspectsOnChange(event: ChangeEvent): void {
        this.syncOverviewFilters();
    }

    switchIncludeProspectsOnDataDisplay(event: DataDisplayEvent): void {
        this.syncOverviewFilters();
    }

    switchIncludeOpportunitiesOnChange(event: ChangeEvent): void {
        this.syncOverviewFilters();
    }

    switchIncludeOpportunitiesOnDataDisplay(event: DataDisplayEvent): void {
        this.syncOverviewFilters();
    }

    syncOverviewFilters() {
        const includeProspects = this.activeRow?.getBoolean("include_prospects") ?? false;
        const includeOppertunities = this.activeRow?.getBoolean("include_opportunities") ?? false;

        this.panelOverviewFilters.visible = includeProspects || includeOppertunities;
        this.panelCustProspOpp.visible = includeProspects || includeOppertunities;
        this.panelCustProsp.visible = includeProspects;
        const setFalse = (comp) => {
            if (comp instanceof Switch) {
                comp.checked = false;
                this.activeRow?.set(comp.field, false);
            }
        };
        if (!this.panelCustProspOpp.visible) {
            this.panelCustProspOpp.forEveryChildComponent(setFalse);
        }
        if (!this.panelCustProsp.visible) {
            this.panelCustProsp.forEveryChildComponent(setFalse);
        }

    }

    switchFilterSalesManagerOnChange(event: ChangeEvent): void {
        this.requireTextboxOnModelField(this.textboxSalesManagerId, "overview_sales_manager");
    }

    switchFilterSalespersonOnChange(event: ChangeEvent): void {
        this.requireTextboxOnModelField(this.textboxSalespersonId, "overview_salesperson");
    }

    switchFilterSalesStatusOnChange(event: ChangeEvent): void {
        this.requireTextboxOnModelField(this.textboxStatus, "overview_sales_status");
    }

    switchFilterOpsUserOnChange(event: ChangeEvent): void {
        this.requireTextboxOnModelField(this.textboxOperationsUser, "overview_ops_user");
    }

    switchFilterProspectTypeOnChange(event: ChangeEvent): void {
        this.requireTextboxOnModelField(this.textboxProspectTypeId, "overview_prospect_type");
    }

    requireTextboxOnModelField(tb:Textbox, booleanName:string): void {
        tb.required = this.activeRow?.getBoolean(booleanName) ?? false;
    }

    isSwitchIncludeProspectValid() {
        if (this.switchIncludeProspects.checked) {
            const isCustomerNull = this.activeRow?.isNull("customer_id");
            const setFilters: Switch[] = [];
            this.panelOverviewFilters.forEveryChildComponent((comp) => {
                if (comp instanceof Switch 
                    && comp.visible
                    && comp.checked) {
                    setFilters.push(comp);
                }
            });
            if (isCustomerNull && setFilters.length === 0) {
                return false;
            }
        }
        return true;
    }

    isSwitchIncludeOpportunitiesValid() {
        if (this.switchIncludeOpportunities.checked) {
            const isOpportunityNull = this.activeRow?.isNull("opportunity_id");
            const setFilters: Switch[] = [];
            this.panelCustProspOpp.forEveryChildComponent((comp) => {
                if (comp instanceof Switch 
                    && comp.visible
                    && comp.checked) {
                    setFilters.push(comp);
                }
            });
            if (isOpportunityNull && setFilters.length === 0) {
                return false;
            }
        }
        return true;
    }

    validate(checkRequired?: boolean, showErrors?: boolean): ValidationResult[] {
        const results = super.validate(checkRequired, showErrors);
        this.validateOverviewIncludes(results);
        return results;
    }

    sourceCallListProfileOnValidate(event: DataSourceValidationEvent) {
        this.validateOverviewIncludes(event.validationResults);
    }

    validateOverviewIncludes(results: ValidationResult[]) {
        if (!this.isSwitchIncludeProspectValid()) {
            const message = "Customer(s)/Prospect(s) or an overview filter is required to include those without Pending Activities."
            results.push({
                isValid: false,
                component: this.textboxCustomerId,
                caption: message,
                validationMessage: message,
                componentToFocus: this.textboxCustomerId
            });
            this.textboxCustomerId.validationWarning = message;

        }
        if (!this.isSwitchIncludeOpportunitiesValid()) {
            const message = "Opportunities  or an overview filter is required to include those without Pending Activities ";
            results.push({
                isValid: false,
                component: this.textboxOpportunityId,
                caption: message,
                validationMessage: message,
                componentToFocus: this.textboxCustomerId
            });
            this.textboxOpportunityId.validationWarning = message;

        }
    }
}